import { useEffect, useState } from "react";
import axios from "axios"

import { statusIsGood } from "../../../helpers/http";
import "../../../css/Submissions.scss";
import FileInput from "../../HelperComponents/FileInput";
import { IoAlertCircle, IoCheckmarkCircle, IoWarning } from "react-icons/io5";
import useIsMounted from "../../hooks/useIsMounted";
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

const fileTypes = ["XLSX"];

export default function Submissions({ user }) {
    const [refetchTrigger, setRefetchTrigger] = useState(false);
    const [importInfo, setImportInfo] = useState(null);
    const isMounted = useIsMounted();

    const pollFrequencyMs = 2000;


    function triggerRefetch() {
        setRefetchTrigger(!refetchTrigger);
    }

    return (
        <div className="submissions-container">
            <div className="page-body">
                <h1>Submissions</h1>
                <div className="submissions-split">
                    <SubmissionImport trigger={triggerRefetch}/>
                    <SubmissionExport refetchTrigger={refetchTrigger}/>
                </div>
                <UsageDocs />
            </div>
        </div>
    );
}

function SubmissionImport({ trigger }) {
    // Progress Towards Degree Data
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    // 0 = simple message, 1 = warning, 2 = error, 3 = success
    const [errorSeverity, setErrorSeverity] = useState('');
    const [error, setError] = useState(null);

    const [open, setOpen] = useState(false);

    // Data Warehouse Data
    const [fullDataFile, setFullDataFile] = useState(null);
    const [fullDataLoading, setFullDataLoading] = useState(false);
    // 0 = simple message, 1 = warning, 2 = error, 3 = success
    const [fullDataErrorSeverity, setFullDataErrorSeverity] = useState(0);
    const [fullDataError, setFullDataError] = useState(null);

    function displayMessage(message, errorSeverity) {
        setError(message);
        setErrorSeverity(errorSeverity);
    }

    function handleChange(files) {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            const fileExtension = selectedFile.name.split('.').pop().toUpperCase();

            if (!fileTypes.includes(fileExtension)) {
                displayMessage("File uploaded was not XLSX!", 2);
                return;
            }

            setFile(selectedFile);
            displayMessage(null);
        } else {
            setFile(null);
            displayMessage('No file selected', 2);
        }
    };

    function handleFullDataChange(files) {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            const fileExtension = selectedFile.name.split('.').pop().toUpperCase();

            if (!fileTypes.includes(fileExtension)) {
                displayMessage("File uploaded was not XLSX!", 2);
                return;
            }

            setFullDataFile(selectedFile);
            displayMessage(null);
        } else {
            setFullDataFile(null);
            displayMessage('No file selected', 2);
        }
    };

    async function handleUpload() {
        if (!file) {
            displayMessage("Please upload a Progress Towards Degree file.", 'error');
            return;
        }

        if (!fullDataFile) {
            displayMessage("Please upload a Full Data From Data Warehouse file.", 'error');
            return;
        }

        setLoading(true);
        displayMessage(null, null);

        const formData = new FormData();
        formData.append("files", file ?? {});
        formData.append("files", fullDataFile ?? {});

        await axios.post("/api/submission", formData).then(() => {
            displayMessage('Processed Submission!', 'success');
            setOpen(true)
            setLoading(false)
            trigger()
            setFile(null);
            setFullDataFile(null);
        }).catch((error) => {
                const errorMessage = error.response?.data?.message;
                
                console.log(errorMessage)

                if (errorMessage.includes("Cannot read properties of undefined (reading 'map')")) {
                    displayMessage(`Incomplete Checksheet`, 'error');
                }
                else {
                    displayMessage(`Error while processing the file`, 'error');
                }

                setOpen(true);
                setLoading(false);

                setFile(null);
                setFullDataFile(null);
            }
        );
    };

    function onTypeError(err) {
        displayMessage("Invalid file type. Please upload an .xlsx (Excel) file.", 'error');
        setFile(null);
    };

    function onSizeError(file) {
        displayMessage(`File size error for ${file.name}.`, 'error');
        setFile(null);
    };

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
          return;
        }

        setOpen(false);
    };

    return (
        <div>
            <Snackbar open={open} autoHideDuration={8000} onClose={handleClose}>
                <Alert
                    onClose={handleClose}
                    severity={errorSeverity}
                    sx={{ width: '100%' }}
                >
                    {error}
                </Alert>
            </Snackbar>
            <h2>Import</h2>
            <h3>Progress Towards Degree:</h3>
            <div className="file-uploader">
                <FileInput
                    file={file}
                    fileTypes={fileTypes}
                    handleChange={handleChange}
                    errorMsg={error}
                    onSizeError={onSizeError}
                    onTypeError={onTypeError}
                    clearFile={() => {
                        setFile(null);
                        displayMessage(null);
                    }}
                />
            </div>
            <h3>Full Data From Data Warehouse:</h3>
            <div className="file-uploader">
                <FileInput
                    file={fullDataFile}
                    fileTypes={fileTypes}
                    handleChange={handleFullDataChange}
                    errorMsg={fullDataError}
                    onSizeError={onSizeError}
                    onTypeError={onTypeError}
                    clearFile={() => {
                        setFullDataFile(null);
                        displayMessage(null);
                    }}
                />
            </div>
            <button className="form-btn" onClick={handleUpload} disabled={loading || (!file && !fullDataFile) }>
                {loading ? (
                    <>
                        <div className="spinner-border" role="status"></div>
                        Processing, please wait...
                    </>
                ) : (
                    "Click to Upload"
                )}
            </button>


        </div>
    )
}

function SubmissionExport({ refetchTrigger, importInfo }) {
    const [submissionHistory, setSubmissionHistory] = useState([]);
    const [downloading, setDownloading] = useState(false);

    const isProcessing = importInfo?.isProcessing || false;
    const initiatingUser = importInfo?.info?.initiatingUser || null;

    function dateToString(date) {
        const year = date.getFullYear().toString().padStart(4, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const hour = date.getHours().toString().padStart(2, '0');
        const min = date.getMinutes().toString().padStart(2, '0');
        const sec = date.getSeconds().toString().padStart(2, '0');
        return `${year}${month}${day}-${hour}${min}${sec}`
    }

    function downloadSubmission() {
        setDownloading(true);
        axios.get("/api/submission", {
            responseType: 'blob',
            params: {
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            }
        }).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const submissionDate = new Date(submissionHistory[0]?.timestamp) || new Date(Date.now());

            // Download file
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `p91-export-${dateToString(submissionDate)}.xlsx`);
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }).catch((err) => {
            if (err?.isAxiosError) {
                const errMsg = err?.response?.data?.message || null;
                alert(`Error exporting/downloading the last submission${errMsg ? `: ${errMsg}` : '. Please try again.'}`);
            } else {
                console.error('Error downloading last submission:', err);
                alert('Error downloading the last submission. Please try again.');
            }
        }).finally(() => setDownloading(false));
    }

    function getSubmissionHistory() {
        axios.get("/api/submission/history").then((response) => {
            if (statusIsGood(response?.status)) {
                setSubmissionHistory(response?.data?.history || []);
            } else {
                console.error(
                    "Error getting submission history:",
                    response?.body?.message
                );
                setSubmissionHistory([]);
            }
        }).catch((err) => {
            console.error("Error getting submission history: ", err);
            setSubmissionHistory([]);
        });
    }

    /**
     * Helper function to format a number of milliseconds into a human-readable
     * time duration 
     */
    function formatMs(ms) {
        let sec = Math.floor(ms / 1000);
        if (sec > 0) {
            ms %= 1000;

            let min = Math.floor(sec / 60);
            if (min > 0) {
                sec %= 60;

                let hr = Math.floor(min / 60);
                if (hr > 0) {
                    min %= 60;
                    return `${hr}h ${min}min ${sec}s`;
                } else {
                    return `${min}min ${sec}s`;
                }
            } else {
                if (ms === 0) {
                    return `${sec}s`;
                } else {
                    return `${sec}s ${ms}ms`;
                }
            }
        } else {
            return `${ms}ms`;
        }
    }

    useEffect(() => {
        getSubmissionHistory();
    }, [refetchTrigger]);

    const hasSubmissionHistory = submissionHistory?.length > 0;

    return (
        <div>
            <h2>Export</h2>
            <button className="form-btn" onClick={downloadSubmission} disabled={!hasSubmissionHistory || downloading || isProcessing}>
                {downloading ? (
                    <>
                        <div className="spinner-border" role="status"></div>
                        Processing, please wait...
                    </>
                ) : (
                    "Download latest submission"
                )}
            </button>
            {hasSubmissionHistory ?
                <>
                    <h3>Submission history</h3>
                    <div className="submission-history">
                        <table>
                            <thead>
                                <tr>
                                    <th>Submitted by</th>
                                    <th>Submitted on</th>
                                    <th>File name</th>
                                    <th>Processing time</th>
                                    <th>Students in violation</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isProcessing &&
                                    <tr>
                                        <td>{initiatingUser || "N/A"}</td>
                                        <td>Processing...</td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                }
                                {submissionHistory.map(
                                    ({
                                        initiating_user_pid, timestamp,
                                        filename, processing_time_ms,
                                        num_students, num_flagged_students
                                    }, index) => (
                                        <tr key={index}>
                                            <td>{initiating_user_pid || "N/A"}</td>
                                            <td>{new Date(timestamp).toLocaleString()}</td>
                                            <td>{filename}</td>
                                            <td>{formatMs(processing_time_ms)}</td>
                                            <td>{`${num_flagged_students}/${num_students}`}</td>
                                        </tr>
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
                : <label>No submission history.</label>
            }
        </div>
    )
}

function UsageDocs() {
    return (
        <div className="submission-docs">
            <h2>Import File Formats</h2>
            <p>
                This tool requires the import of two <strong>.xlsx</strong> 
                files that you import must be of specific formats.
                <blockquote>
                    Files that come directly from the Progress Toward Degree
                    tool and Data Warehouse should work fine.
                </blockquote>
            </p>
            <h4>
                Progress Towards Degree File
            </h4>
            <p>
                All information to be included in the submission should be
                stored in the first tab of the spreadsheet. Each row, except for
                the first, represents a course a student has taken during a
                semester. The first row is a header that describes the
                information stored in its column. While cells may be blank, the
                import must have the following headers at a minimum:
            </p>
            <ul>
                <li><strong>ID</strong>: The 90* ID number of the student taking the course in the row.</li>
                <li><strong>name</strong>: The name of the student.</li>
                <li><strong>admit_term</strong>: The semester the student was admitted. It is represented as a number formed with the year and the month of the semester (i.e. Fall 2022 is 202209, Spring 2023 is 202301, etc.)</li>
                <li><strong>advisor_last_name</strong>: The last name of the student's advisor.</li>
                <li><strong>standing_message</strong>: The standing message for the student's current semester.</li>
                <li><strong>conc</strong>: The concentration (focus) of the student within CS. If left blank, defaults to "CS".</li>
                <li><strong>overall_hrs_attempted</strong>: The total number of hours attempted by the student.</li>
                <li><strong>overall_gpa</strong>: The overall GPA of the student.</li>
                <li><strong>transfer_hrs_earned</strong>: The total number of transfer hours earned by the student.</li>
                <li><strong>subj_expanded</strong>: The subject of the course in this row (CS, MATH, etc.).</li>
                <li><strong>crse_numb</strong>: The course number of the course in this row.</li>
                <li><strong>term_taken</strong>: The course number of the course in this row.</li>
                <li><strong>credit_hours</strong>: The number of credit hours the course counts for.</li>
                <li><strong>grade</strong>: The grade earned by the student for this course in that semester.</li>
            </ul>
            <h4>
                Data Warehouse File
            </h4>
            <p>
                All information to be included in the submission should be
                stored in the first tab of the spreadsheet. Each row, except for
                the first, represents a student. The first row is a header that describes the
                information stored in its column. While cells may be blank, the
                import must have the following headers at a minimum:
            </p>
            <ul>
                <li><strong>STUDENT ID</strong>: The 90* ID number of the student taking the course in the row.</li>
                <li><strong>COMPLETION TERM CODE</strong>: Code indicating graduation time of student.</li>
                <li><strong>EMAIL ADDRESS</strong>: The email address of the student.</li>
            </ul>
            <p>
                Upon uploading the files, you and any other advisors logged into
                the site will be able to see the analyzed information and
                export a corresponding <strong>.xlsx</strong> file for the most
                recent submission.
            </p>
            <h2>Export File Format</h2>
            <p>
                The <strong>.xlsx</strong> file that you receive on export
                contains all of the analyzed information from the last
                submission. Similarly to the import file, student and course
                information are shown in each row of the spreadsheet. The
                exported file will note classes a student has not completed
                that are required for graduation as determined by the checksheet.
                Additionally, the file will show any flags associated with each
                student/course that indicate a Policy 91 violation (or it shows
                a warning if a student is close to being in violation). 
            </p>
            <p>
                There are four types of violations, as defined by the
                expectations of the Satisfactory Progress Toward Degree section
                of the CS checksheet:
            </p>
            <ol>
                <li><strong>Student's in-major GPA is less than 2.0</strong></li>
                <ul><li>
                    Denoted in the export file with highlighting on a student's in-major GPA
                </li></ul>
                <li><strong>Student is not taking a 3-credit course required for their major in their current semester</strong></li>
                <ul><li>
                    Denoted with highlighting on all courses in a student's current semester
                </li></ul>
                <li><strong>Student has repeated a required CS course more than twice</strong></li>
                <ul>
                    <li>Denoted with a highlighted message in the "Flag" column next to their most recent repeat of the course.</li>
                    <li>Warnings for a third repeat will also appear next to the course's most recent repeat.</li>
                </ul>
                <li><strong>Student has repeated more than three required CS courses</strong></li>
                <ul>
                    <li>Denoted with a message in the "Flag" column next to the most recent repeat of each repeated  course.</li>
                    <li> Warnings for this violation will appear next to any courses that will need a third repeat.</li>
                </ul>
            </ol>
            <p>
                The first tab in the spreadsheet includes all students who were
                included in the input file for analysis. Each successive tab
                contains the same data, but each tab is labeled and filtered to
                only include students for each individual advisor.
            </p>
        </div>
    )
}
